import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { BlacklistScope } from "@/enum/blacklist.enum";
import { useCreateBlacklistMutation } from "@/pages/Private/redux/blacklist/blacklist.api";

import { CreateBlacklist, CreateBlacklistSchema } from "../../Blacklist/schema/blacklist";

interface CreateBlacklistModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	isGlobal: boolean;
	isAdmin: boolean;
}

export const CreateBlacklistModal: FunctionComponent<CreateBlacklistModalProps> = ({
	isOpen,
	setIsOpen,
	isGlobal,
	isAdmin,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`contacts.blacklist.${key}`);
	const [createBlacklist, { isLoading, error }] = useCreateBlacklistMutation();

	const initialValues: CreateBlacklist = {
		domain: "",
		email: "",
		linkedinUrl: "",
		linkedinCompanyUrl: "",
		isGlobal,
	};

	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateBlacklist>({
		defaultValues: initialValues,
		resolver: zodResolver(CreateBlacklistSchema),
	});

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateBlacklist>>(initialValues);

	const handleSaveFormState = (value: string, name: keyof CreateBlacklist) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateBlacklist) => {
		const payload = { ...values };

		// Remove empty strings — backend expects undefined/omitted, not ""
		if (!payload.domain) {
			delete payload.domain;
		}

		if (!payload.email) {
			delete payload.email;
		}

		if (!payload.linkedinUrl) {
			delete payload.linkedinUrl;
		}

		if (!payload.linkedinCompanyUrl) {
			delete payload.linkedinCompanyUrl;
		}

		try {
			await createBlacklist(payload).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const scopeOptions: AutoCompleteItem[] = [
		{ id: "auto", title: ts("scopeAuto") },
		{ id: BlacklistScope.PERSON, title: ts("scopePerson") },
		{ id: BlacklistScope.ORGANIZATION, title: ts("scopeOrganization") },
	];

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={ts("createTitle")}
		>
			<div className="flex flex-col gap-4">
				<InputField
					error={!!errors.domain?.message}
					handleChange={(event) => handleSaveFormState(event.target.value, "domain")}
					label={ts("domain")}
					name="domain"
					placeholder="example.com"
					value={currentFormState.domain || ""}
				/>

				<InputField
					error={!!errors.email?.message}
					handleChange={(event) => handleSaveFormState(event.target.value, "email")}
					label={ts("email")}
					name="email"
					placeholder="john@example.com"
					value={currentFormState.email || ""}
				/>

				<InputField
					error={!!errors.linkedinUrl?.message}
					handleChange={(event) => handleSaveFormState(event.target.value, "linkedinUrl")}
					label={ts("linkedinUrl")}
					name="linkedinUrl"
					placeholder="https://linkedin.com/in/..."
					value={currentFormState.linkedinUrl || ""}
				/>

				<InputField
					error={!!errors.linkedinCompanyUrl?.message}
					handleChange={(event) => handleSaveFormState(event.target.value, "linkedinCompanyUrl")}
					label={ts("linkedinCompanyUrl")}
					name="linkedinCompanyUrl"
					placeholder="https://linkedin.com/company/..."
					value={currentFormState.linkedinCompanyUrl || ""}
				/>

				<Dropdown
					data={scopeOptions}
					defaultValue={scopeOptions[0]}
					handleSelect={(value: AutoCompleteItem) => {
						if (value?.id === "auto") {
							setValue("scope", undefined);
						} else if (value?.id) {
							setValue("scope", value.id as BlacklistScope);
						}
					}}
					label={ts("scope")}
					name="scope"
				/>

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
		</Modal>
	);
};
